import { useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";
import { URL } from "../../store/utils";
import { handleSocketSessionExpiry } from "../../utils/dashboardUtils";
import useLatestToken, { attachTokenOnReconnect } from "../useLatestToken";
import { mergeAlertsPayload } from "../../utils/alertCategories";

/**
 * Uyarılar sayfası verisi (#229) — dashboard socket'i üzerinden
 * `getAlertsData` → `alertsData`. Sunucu, kaynak veri yenilendikçe (MQTT)
 * aynı olayı kendiliğinden yeniden iter; sayfa açık kaldıkça canlıdır.
 *
 * Sözleşme: Workspace docs/api-contracts/dashboard-alerts.md
 *
 * @returns {{ data: object|null, isLoading: boolean, isConnected: boolean,
 *   error: string|null, refresh: () => void }}
 */
export default function useAlerts(token) {
  const { hasToken, tokenRef } = useLatestToken(token);
  const socketRef = useRef(null);
  const [data, setData] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);

  const request = useCallback(() => {
    if (socketRef.current?.connected) socketRef.current.emit("getAlertsData");
  }, []);

  useEffect(() => {
    if (!hasToken) return undefined;

    const socket = io(URL.url, {
      transports: ["websocket", "polling"],
      timeout: 5000,
      query: { token: tokenRef.current },
    });
    socketRef.current = socket;
    attachTokenOnReconnect(socket, tokenRef);

    // Sunucu handler'ları "authenticated"tan sonra kurulur; istek ancak o
    // zaman dinlenir. Yeniden bağlanınca da tekrar gelir → veri tazelenir.
    socket.on("authenticated", () => {
      setIsConnected(true);
      socket.emit("getAlertsData");
    });

    // Kademeli gönderim (#238): her dilim hazır oldukça yeni yük gelir;
    // yeniden hesaplanan dilimde eldeki veri korunur (titreme olmasın).
    socket.on("alertsData", (payload) => {
      setData((prev) => mergeAlertsPayload(prev, payload));
      setError(null);
    });

    socket.on("error", (payload) => {
      if (payload?.reason) {
        setIsConnected(false);
        if (handleSocketSessionExpiry(payload.reason)) return;
      }
      setError(payload?.message || "Uyarı verisi alınamadı");
    });

    socket.on("disconnect", () => setIsConnected(false));

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [hasToken, tokenRef]);

  return {
    data,
    isLoading: data == null && !error,
    isConnected,
    error,
    refresh: request,
  };
}
